import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';

import {
    SimpleButton,
    SimpleTextField,
    ReportScoringBandsLegend,
    Loading,
    ErrorMessage,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import { getPdfFileName } from '@/src/utils';
import StudentCompletionStatusReportDataTable from '@/src/pages/reports/completionStatusReport/components/StudentCompletionStatusReportDataTable';
import ConfirmActionModal from '@/src/pages/reports/completionStatusReport/components/ConfirmActionModal';
import type { AssignmentStudentDetailDTO } from '@/src/pages/reports/completionStatusReport/models/completionStatusReportDTOs';

type PendingConfirmAction = 'forceSubmit' | 'unsubmit' | 'delete' | null;

interface CompletionStatusReportModalProps {
    examAssignmentId: number;
    examName: string;
    // One of classIds OR classId may be provided. isByExam + examId together
    // drive the by-exam fetch mode used by the Completion Status report rows.
    classIds?: number[];
    classId?: number;
    className?: string;
    isByExam?: boolean;
    examId?: number;
    open: boolean;
    onClose: () => void;
}

// Single source of truth for the student-level completion-status UI:
// fetch, freeze, force-submit, unsubmit, delete, selection, search,
// scoring bands. Used from the Completion Status Report page and from
// the Assign a Test page.
export default function CompletionStatusReportModal({
    examAssignmentId,
    examName,
    classIds,
    classId,
    className,
    isByExam,
    examId,
    open,
    onClose,
}: CompletionStatusReportModalProps) {
    const [searchString, setSearchString] = useState('');
    const [data, setData] = useState<Array<AssignmentStudentDetailDTO>>([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [selectedStudentIds, setSelectedStudentIds] = useState<Set<number>>(new Set());
    const [forceSubmitLoading, setForceSubmitLoading] = useState(false);
    const [freezeSelectedLoading, setFreezeSelectedLoading] = useState(false);
    const [unsubmitSelectedLoading, setUnsubmitSelectedLoading] = useState(false);
    const [deleteSelectedLoading, setDeleteSelectedLoading] = useState(false);
    const [pendingAction, setPendingAction] = useState<PendingConfirmAction>(null);
    const [showReportingBands, setShowReportingBands] = useState(false);

    const allSelectedFrozen = useMemo(
        () =>
            selectedStudentIds.size > 0 &&
            data
                .filter((r) => selectedStudentIds.has(r.studentUserId))
                .every((r) => r.isTeacherFrozen === true),
        [data, selectedStudentIds]
    );

    // Submitted / not-submitted counts among the currently-selected rows.
    // Drives the disabled state on Force Submit / Unsubmit Selected.
    const { selectedSubmittedCount, selectedUnsubmittedCount } = useMemo(() => {
        let submitted = 0;
        let unsubmitted = 0;
        for (const r of data) {
            if (!selectedStudentIds.has(r.studentUserId)) continue;
            if (r.isSubmitted) submitted++;
            else unsubmitted++;
        }
        return { selectedSubmittedCount: submitted, selectedUnsubmittedCount: unsubmitted };
    }, [data, selectedStudentIds]);
    const nSelected = selectedStudentIds.size;

    const fetchData = useCallback(async () => {
        setLoaded(false);
        setError('');

        if (!examAssignmentId || Number.isNaN(examAssignmentId)) {
            setError('Missing assessment assignment ID.');
            setLoaded(true);
            return;
        }

        // Build the request URL(s) covering this fetch's scope. The classIds
        // branch fans out one request per class; everything else is a single
        // call.
        const base = `reports/completionStatusReport/assignmentDetails?examAssignmentId=${examAssignmentId}`;
        let urls: string[];
        if (isByExam) {
            urls = [base + (examId ? `&isByExam=true&examId=${examId}` : `&isByExam=true`)];
        } else if (classIds && classIds.length > 0) {
            urls = classIds.map((cid) => `${base}&classId=${cid}`);
        } else if (classId) {
            urls = [`${base}&classId=${classId}`];
        } else {
            urls = [base];
        }

        try {
            const results = await Promise.all(
                urls.map((u) => apiRequest<Array<AssignmentStudentDetailDTO>>('GET', u))
            );
            setData(results.flatMap((r) => r.data ?? []));
        } catch {
            setError('Failed to load assignment student details');
        } finally {
            setLoaded(true);
        }
    }, [examAssignmentId, classIds, classId, isByExam, examId]);

    useEffect(() => {
        if (open) fetchData();
    }, [open, fetchData]);

    function handleToggleSelect(studentUserId: number) {
        setSelectedStudentIds((prev) => {
            const next = new Set(prev);
            if (next.has(studentUserId)) next.delete(studentUserId);
            else next.add(studentUserId);
            return next;
        });
    }

    function handleSelectAll(ids: number[]) {
        setSelectedStudentIds(new Set(ids));
    }

    async function handleFreezeSelected() {
        if (!examAssignmentId || selectedStudentIds.size === 0) return;
        setFreezeSelectedLoading(true);
        setError('');
        const targetFrozen = !allSelectedFrozen;
        try {
            await Promise.all(
                Array.from(selectedStudentIds).map((studentUserId) =>
                    apiRequest('POST', 'reports/completionStatusReport/freeze/student', {
                        examAssignmentId,
                        studentUserId,
                        isFrozen: targetFrozen,
                    })
                )
            );
            setSelectedStudentIds(new Set());
            await fetchData();
        } catch {
            setError('Failed to freeze selected students.');
        } finally {
            setFreezeSelectedLoading(false);
        }
    }

    async function handleForceSubmit() {
        if (!examAssignmentId || selectedStudentIds.size === 0) return;
        setForceSubmitLoading(true);
        setError('');
        try {
            await apiRequest('POST', 'reports/completionStatusReport/forceSubmit', {
                examAssignmentId,
                studentUserIds: Array.from(selectedStudentIds),
            });
            setSelectedStudentIds(new Set());
            await fetchData();
        } catch {
            setError('Failed to force submit.');
        } finally {
            setForceSubmitLoading(false);
        }
    }

    async function handleUnsubmitSelected() {
        if (!examAssignmentId || selectedStudentIds.size === 0) return;
        setUnsubmitSelectedLoading(true);
        setError('');
        try {
            await apiRequest('POST', 'reports/completionStatusReport/unsubmit', {
                examAssignmentId,
                studentUserIds: Array.from(selectedStudentIds),
            });
            setSelectedStudentIds(new Set());
            await fetchData();
        } catch {
            setError('Failed to unsubmit selected attempts.');
        } finally {
            setUnsubmitSelectedLoading(false);
        }
    }

    // Endpoint TBD — placeholder URL. Backend must support this.
    async function handleDeleteSelected() {
        if (!examAssignmentId || selectedStudentIds.size === 0) return;

        // Every student in the assignment has an attempt row, so we can collect
        // attempt IDs directly without filtering.
        const attemptIds = data
            .filter((r) => selectedStudentIds.has(r.studentUserId))
            .map((r) => r.id);

        setDeleteSelectedLoading(true);
        setError('');
        try {
            await apiRequest('POST', 'reports/completionStatusReport/deleteAttempts', {
                attemptIds,
            });
            setSelectedStudentIds(new Set());
            await fetchData();
        } catch {
            setError('Failed to delete selected attempts.');
        } finally {
            setDeleteSelectedLoading(false);
        }
    }

    // Confirmation modal config for whichever destructive action is pending.
    const confirmConfig = (() => {
        const plural = nSelected === 1 ? '' : 's';
        switch (pendingAction) {
            case 'forceSubmit':
                return {
                    title: 'Force Submit',
                    message: `Are you sure you want to force submit ${nSelected} student${plural}?`,
                    confirmLabel: 'Force Submit',
                    confirmColor: 'warning' as const,
                    execute: handleForceSubmit,
                };
            case 'unsubmit':
                return {
                    title: 'Unsubmit Attempts',
                    message: `Unsubmit ${nSelected} attempt${plural}? The student${plural} will be able to keep working.`,
                    confirmLabel: 'Unsubmit',
                    confirmColor: 'warning' as const,
                    execute: handleUnsubmitSelected,
                };
            case 'delete':
                return {
                    title: 'Delete Attempts',
                    message: `Delete ${nSelected} attempt${plural}? This cannot be undone.`,
                    confirmLabel: 'Delete',
                    confirmColor: 'error' as const,
                    execute: handleDeleteSelected,
                };
            default:
                return null;
        }
    })();

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 3,
                        width: '85%',
                        height: '85%',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        Completion Status Report - {examName}
                        {className ? ` • ${className}` : ''}
                    </Typography>

                    <Loading visible={!loaded} message="Loading completion status report..." />
                    <ErrorMessage error={error} style={{ marginBottom: '8px' }} />

                    {loaded && (
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color:
                                                nSelected > 0 ? 'primary.main' : 'text.secondary',
                                            minWidth: 80,
                                        }}
                                    >
                                        {nSelected} selected
                                    </Typography>
                                    <SimpleButton
                                        variant="contained"
                                        color="primary"
                                        disabled={
                                            freezeSelectedLoading ||
                                            nSelected === 0 ||
                                            selectedUnsubmittedCount === 0
                                        }
                                        onClick={handleFreezeSelected}
                                    >
                                        {freezeSelectedLoading
                                            ? 'Loading...'
                                            : allSelectedFrozen && nSelected > 0
                                              ? 'Unfreeze Selected'
                                              : 'Freeze Selected'}
                                    </SimpleButton>
                                    <SimpleButton
                                        variant="contained"
                                        color="warning"
                                        disabled={
                                            forceSubmitLoading ||
                                            nSelected === 0 ||
                                            selectedUnsubmittedCount === 0
                                        }
                                        onClick={() => setPendingAction('forceSubmit')}
                                    >
                                        {forceSubmitLoading
                                            ? 'Submitting...'
                                            : 'Force Submit Selected'}
                                    </SimpleButton>
                                    <SimpleButton
                                        variant="contained"
                                        color="warning"
                                        disabled={
                                            unsubmitSelectedLoading ||
                                            nSelected === 0 ||
                                            selectedSubmittedCount === 0
                                        }
                                        onClick={() => setPendingAction('unsubmit')}
                                    >
                                        {unsubmitSelectedLoading
                                            ? 'Loading...'
                                            : 'Unsubmit Selected'}
                                    </SimpleButton>
                                    <SimpleButton
                                        variant="contained"
                                        color="error"
                                        disabled={deleteSelectedLoading || nSelected === 0}
                                        onClick={() => setPendingAction('delete')}
                                    >
                                        {deleteSelectedLoading ? 'Deleting...' : 'Delete Selected'}
                                    </SimpleButton>
                                    <SimpleButton
                                        variant="outlined"
                                        onClick={() => setShowReportingBands(!showReportingBands)}
                                        aria-expanded={showReportingBands}
                                        aria-label="Toggle reporting bands dropdown"
                                    >
                                        {showReportingBands
                                            ? 'Hide Scoring Bands'
                                            : 'Show Scoring Bands'}
                                    </SimpleButton>
                                </Box>

                                <SimpleTextField
                                    name="student-status-search"
                                    label="Search"
                                    variant="outlined"
                                    value={searchString}
                                    onChange={(e) => setSearchString(e.target.value)}
                                    sx={{ minWidth: 200, maxWidth: 300 }}
                                />
                            </Box>

                            <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                                <ReportScoringBandsLegend
                                    dataLoaded={loaded}
                                    showLegend={showReportingBands}
                                />
                            </Box>

                            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                <StudentCompletionStatusReportDataTable
                                    data={data}
                                    searchString={searchString}
                                    selectedStudentIds={selectedStudentIds}
                                    onToggleSelect={handleToggleSelect}
                                    onSelectAll={handleSelectAll}
                                    pdfFileName={getPdfFileName(
                                        examName,
                                        className,
                                        'Completion Status'
                                    )}
                                />
                            </Box>
                        </Box>
                    )}

                    <Box textAlign="right" mt={2}>
                        <Button variant="outlined" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <ConfirmActionModal
                open={confirmConfig !== null}
                title={confirmConfig?.title ?? ''}
                message={confirmConfig?.message ?? ''}
                confirmLabel={confirmConfig?.confirmLabel}
                confirmColor={confirmConfig?.confirmColor}
                onConfirm={() => {
                    const exec = confirmConfig?.execute;
                    setPendingAction(null);
                    exec?.();
                }}
                onClose={() => setPendingAction(null)}
            />
        </>
    );
}
